import React, { useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import * as styles from './modal.styles';
import type DashboardOffice from '../models/dashboard-office';
import Row from '../../../core/components/row/row.component';
import Column from '../../../core/components/column/column.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import AppConstants from '../../../core/app.constants';
import notify from '../../../core/components/notify/notify.component';
import ContentTypeConstants from '../../../core/content-type-constants/content-type.constants';
import getQRCodesForOfficeAndAmount from '../../offices/services/qrcode-service';
import Input from '../../../core/components/input/input.component';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  readonly dashboardOffice: DashboardOffice;
  readonly handleClose: () => void;
}

const QRCodeGenerationModal = ({ showModal, dashboardOffice, handleClose }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const [isLoading, setIsLoading] = useState(false);
  const keycloak = useKeycloak();
  const [amount, setAmount] = useState(dashboardOffice.totalCapacity);

  const genericError = (): void => {
    notify(t('qr-generation-error-message'), false);
  };

  const inputIsValid = (): boolean => {
    if (!dashboardOffice.id) {
      genericError();
      return false;
    }
    if (amount <= 0) {
      notify(t('qr-zero-amount-message'), false);
      return false;
    }
    if (amount > AppConstants.MAXIMUM_QR_AMOUNT) {
      notify(t('qr-max-amount-exceeded-message').replace('#', AppConstants.MAXIMUM_QR_AMOUNT.toString()), false);
      return false;
    }
    return true;
  };

  const generateQRCodes = async (): Promise<void> => {
    if (!inputIsValid()) {
      return;
    }
    setIsLoading(true);

    getQRCodesForOfficeAndAmount(amount, keycloak)
      .then((response) => {
        if (response && response.blob) {
          return response.blob();
        }

        return undefined;
      })
      .then((blob) => {
        try {
          if (blob === undefined || blob.size <= 0) {
            throw new Error('No file received');
          }

          const url = window.URL.createObjectURL(new Blob([blob], { type: ContentTypeConstants.PDF }));
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', `QRCodes_${dashboardOffice.id}_${moment(new Date()).format('yyyymmddhMMss')}`);
          document.body.appendChild(link);
          link.click();
          link.parentNode?.removeChild(link);
          setIsLoading(false);

          notify(t('qr-generation-success-message').replace('#', amount.toString()).replace('C_NAME', dashboardOffice.name), true);
          handleClose();
        } catch (error) {
          setIsLoading(false);
          genericError();
        }
      });
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} style={styles.modalStyle(isMobile)}>
      <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
        <span className={style(styles.columnHeader)}>{t('qr-codes')}</span>
        <span className={style(styles.bodyText)}>
          {t('number-of-workplaces-message').replace('#', dashboardOffice.totalCapacity.toString()).replace('C_NAME', dashboardOffice.name)}
        </span>
        <Row mainAxisAlignment={MainAxisAlignment.Stretch} crossAxisAlignment={CrossAxisAlignment.Center}>
          <span className={style(styles.bodyText)}>{t('qr-codes-amount')}</span>
          <Input
            value={amount?.toString()}
            onChange={(value): void => {
              setAmount(value.length > 0 ? Number(value) : 0);
            }}
            type="number"
            min={0}
            max={1000}
          />
        </Row>
        <div className={style(styles.button)}>
          <PrimaryButton
            loading={isLoading}
            fullWidth
            onClick={(): void => {
              generateQRCodes();
            }}
          >
            {t('generate')}
          </PrimaryButton>
        </div>
        {isLoading ? (
          <span className={style(styles.bodyText)}>{t('qr-generation-progress-message')}</span>
        ) : (
          <PrimaryButton fullWidth onClick={handleClose}>
            {t('close')}
          </PrimaryButton>
        )}
      </Column>
    </Modal>
  );
};

export default QRCodeGenerationModal;
